import { useEffect, useMemo } from 'react'
import { useBucket } from '@mc/experiments-react'
import { QUIZ_QUERY_PARAMS } from '@mc/find-my-classes'
import { useCookieRedirectHref } from '@mc/growth'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import { trackExperimentViewed } from '@mc/track-event'
import {
  CAROUSEL_SPRINGBOARD_EXP_FLAG,
  type CarouselItem,
  type CarouselItemType,
  type ExperimentVariants,
} from './constants'

export const useTrackCarouselSpringboardExperiment = () => {
  useEffect(() => {
    trackExperimentViewed(CAROUSEL_SPRINGBOARD_EXP_FLAG)
  }, [])
}

export const useCarouselSpringboardExp = () => {
  const bucket = useBucket<ExperimentVariants>(CAROUSEL_SPRINGBOARD_EXP_FLAG)

  return {
    inPen5453Experiment: !!bucket,
    isPen5453Variant: bucket?.startsWith('variant'),
    isPen5453Variant2: bucket === 'variant_2',
  }
}

export const useSpringboardCarouselData = (): CarouselItem[] => {
  const { isPen5453Variant } = useCarouselSpringboardExp()
  const { carouselSpringboardItems = [] } = useStaticData<{
    carouselSpringboardItems: CarouselItem[]
  }>()

  return useMemo(() => {
    if (isPen5453Variant && carouselSpringboardItems.length > 0) {
      return carouselSpringboardItems
    }
    return []
  }, [isPen5453Variant, carouselSpringboardItems])
}

export const useSpringboardTileHref = (
  itemUuid?: string,
  itemType?: CarouselItemType,
  categoryId?: number,
) => {
  const { isPen5453Variant2 } = useCarouselSpringboardExp()
  const cookieRedirectHref = useCookieRedirectHref()

  return useMemo(() => {
    if (cookieRedirectHref) {
      return cookieRedirectHref
    }

    if (isPen5453Variant2 && itemUuid && itemType) {
      const params = new URLSearchParams()

      params.set(QUIZ_QUERY_PARAMS.SPRINGBOARD_ITEM_ID, itemUuid)
      params.set(QUIZ_QUERY_PARAMS.SPRINGBOARD_ITEM_TYPE, itemType)

      // Add primary category ID to preselect the corresponding motivation
      if (categoryId) {
        params.set(QUIZ_QUERY_PARAMS.SELECTED_CATEGORIES, categoryId.toString())
      }

      return `${paths.classRecommendations}?${params.toString()}`
    }

    return paths.classRecommendations
  }, [cookieRedirectHref, isPen5453Variant2, itemUuid, itemType, categoryId])
}
